import { Component } from '@theme/component';
import { CollectionUpdatedEvent, eventBus } from '@theme/events';

/** Touch devices fling several viewports per second and fetch over slower
 *  networks — trigger next-page loads earlier than the configured desktop
 *  distance so the page lands before the user reaches the end of the grid. */
const TOUCH_TRIGGER_MULTIPLIER = (window.matchMedia?.('(hover: none)').matches ?? false) ? 3 : 1;
import { ScrollActions, ScrollClearEvent } from '@theme/scroll--state--actions';
import { FilterActions } from '@theme/collection--filter--state--actions';
import { filterStore } from '@theme/collection--filter--state--store';
import { VirtualChunks } from '@theme/collection--virtual-chunks';
import { parseEmbedSchedule } from '@theme/collection--embed-schedule';

/** Total fetch attempts per page before auto-retry stops. After that, the
 *  failure counter resets so a fresh user-driven intersection (scrolling away
 *  and back) starts a new retry cycle instead of hammering a dead endpoint. */
const MAX_LOAD_ATTEMPTS = 3;

/** Base delay for auto-retry backoff: 1s, 2s, 4s... */
const RETRY_BASE_DELAY_MS = 1000;

const EDITOR_LIVE_CARDS = 8;

/** Per-request ceiling. A hung connection must not strand the component with
 *  #isLoading stuck true forever — abort and let the retry path take over. */
const FETCH_TIMEOUT_MS = 15000;

/**
 * @typedef {Object} InfiniteScrollRefs
 * @property {HTMLElement} grid - The product grid container
 * @property {HTMLElement} [loader] - The loading indicator element
 */

/**
 * Collection Infinite Scroll Web Component
 *
 * Uses Intersection Observer to load more products as the user scrolls.
 * Fetches the next page URL and appends product cards to the grid.
 * Tracks current page for scroll restoration and resets on filter changes.
 *
 * @attr {string} data-next-url - URL to fetch the next page of products
 * @attr {number} data-current-page - Current page number (updated as pages load)
 * @attr {string} data-section-id - Section ID for Section Rendering API fetches (read by the scroll store's grid rebuild)
 * @attr {number} data-load-trigger-vh - Distance from viewport bottom to trigger load (in vh units, default: 50; multiplied ×3 on touch devices)
 *
 * @example
 * <collection-infinite-scroll data-next-url="/collections/all?page=2" data-current-page="1" data-load-trigger-vh="50">
 *   <div ref="grid" data-product-grid>
 *     <!-- product cards -->
 *   </div>
 *   <div ref="loader">Loading...</div>
 * </collection-infinite-scroll>
 *
 * @extends {Component<InfiniteScrollRefs>}
 */
class CollectionInfiniteScroll extends Component {
	/** @type {IntersectionObserver|null} */
	#observer = null;

	/** @type {AbortController|null} */
	#abortController = null;

	/** @type {boolean} */
	#isLoading = false;

	/** @type {string|null} */
	#nextUrl = null;

	/** @type {number} */
	#currentPage = 1;

	/** @type {number} */
	#loadTriggerVh = 50;

	/** @type {Set<string>} URLs already fetched — prevents duplicate page loads */
	#fetchedUrls = new Set();

	/** @type {() => void} */
	#unsubscribeRestored = () => {};

	/** @type {() => void} */
	#unsubscribeRestoreFailed = () => {};

	/** @type {() => void} */
	#unsubscribeFilterApplied = () => {};

	/** @type {() => void} */
	#unsubscribeFilterStatus = () => {};

	/** @type {import('@theme/collection--virtual-chunks').VirtualChunks|null} */
	#virtualizer = null;

	/** @type {Array<{afterPage:number,targetUrl:string,pageCount:number,bannerId:string|null,fired:boolean}>} */
	#schedule = [];

	/** @type {{rule:object,nextUrl:string|null,loaded:number}|null} active embed injection */
	#injection = null;

	/** @type {number} bumped on every filter reset — an in-flight page load from
	 *  the previous result set must not commit after the grid was replaced */
	#generation = 0;

	/** @type {number} consecutive failed page loads — resets on success or filter change */
	#failedAttempts = 0;

	/** @type {number} pending auto-retry timer id (0 = none) */
	#retryTimer = 0;

	connectedCallback() {
		super.connectedCallback();

		this.#abortController = new AbortController();
		this.#nextUrl = this.dataset.nextUrl || null;
		this.#currentPage = parseInt(this.dataset.currentPage, 10) || 1;
		this.#loadTriggerVh = parseInt(this.dataset.loadTriggerVh, 10) || 50;

		if (!this.refs.grid) {
			console.error('CollectionInfiniteScroll: Missing [ref="grid"] element');
			return;
		}

		// The scroll store and embed schedule address "the" grid via a bare
		// document.querySelector — two instances on one page would fight over
		// that shared state. Warn rather than silently misbehave.
		if (document.querySelectorAll('collection-infinite-scroll').length > 1) {
			console.warn(
				'CollectionInfiniteScroll: multiple instances on one page are not supported — ' +
				'scroll restoration and embed interleaving assume a single grid.'
			);
		}

		// Bound the DOM: recycle off-screen page bands as the grid grows.
		this.#virtualizer = new VirtualChunks(this.refs.grid);
		this.#virtualizer.start();

		// Phase 2: read the embed interleave schedule (inject N pages of another
		// collection after a given host page).
		this.#schedule = this.#readSchedule();

		// Listen for scroll restoration events
		this.#unsubscribeRestored = eventBus.on(ScrollActions.restored, (event) => {
			this.#handleScrollRestored(event);
		});

		this.#unsubscribeRestoreFailed = eventBus.on(ScrollActions.restoreFailed, () => {
			this.removeAttribute('data-restore-pending');
			// Re-arm even on failed restore so scrolling still loads more.
			if (this.#nextUrl || this.refs.grid?.querySelector('template[data-deferred-product]')) {
				this.#armLoader();
			}
		});

		// Listen for filter applied to reset pagination with the new nextUrl
		this.#unsubscribeFilterApplied = eventBus.on(FilterActions.applied, (event) => {
			this.#handleFilterReset(event.detail.data.nextUrl);
		});

		// Self-manage data-filtering attribute via store status subscription
		this.#unsubscribeFilterStatus = filterStore.subscribe(
			(s) => s.status,
			(status) => {
				this.toggleAttribute('data-filtering', status === 'loading');
			}
		);

		// Don't start loading if a scroll restore is pending — the scroll store
		// will morph the grid and emit restored/restoreFailed when done.
		const restorePending = !!document.getElementById('scroll-restore-hide')
			|| this.hasAttribute('data-restore-pending');

		if (restorePending) {
			// If restore already completed (event fired before we loaded),
			// self-sync from data attributes left by the scroll store.
			if (!document.getElementById('scroll-restore-hide') && this.hasAttribute('data-restore-pending')) {
				this.#selfSyncFromRestore();
			}
		} else if (this.#nextUrl || this.refs.grid.querySelector('template[data-deferred-product]')) {
			this.#armLoader();
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#observer?.disconnect();
		this.#observer = null;
		this.#abortController?.abort();
		this.#abortController = null;
		clearTimeout(this.#retryTimer);
		this.#retryTimer = 0;
		this.#unsubscribeRestored();
		this.#unsubscribeRestoreFailed();
		this.#unsubscribeFilterApplied();
		this.#unsubscribeFilterStatus();
		this.#virtualizer?.stop();
		this.#virtualizer = null;
	}

	/**
	 * Trigger distance in px below the viewport. IntersectionObserver only
	 * accepts px/% in rootMargin, so vh is converted against the CURRENT
	 * viewport height (recomputed each arm — mobile URL-bar collapse changes it).
	 * @returns {number}
	 */
	#triggerPx() {
		return Math.round((this.#loadTriggerVh * TOUCH_TRIGGER_MULTIPLIER / 100) * window.innerHeight);
	}

	/**
	 * Arm the loader observer so the next intersection triggers a load, and show
	 * the loader. No-ops when the loader is absent.
	 *
	 * Invariant: `#observer !== null` ⇔ the loader is armed. ALWAYS rebuilds —
	 * an IntersectionObserver fires only on intersection TRANSITIONS, so when the
	 * loader is already inside the trigger margin (e.g. after a filter morph, a
	 * page append, or a deferred reveal) re-observing is the only way to force a
	 * fresh intersection check. This single method replaces the old
	 * setup/re-observe split that the stuck-pagination bug grew out of.
	 */
	#armLoader() {
		const { loader } = this.refs;
		if (!loader) return;

		loader.removeAttribute('hidden');
		this.#observer?.disconnect();
		this.#observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && !this.#isLoading) this.#loadMore();
			},
			{ rootMargin: `0px 0px ${this.#triggerPx()}px 0px`, threshold: 0 }
		);
		this.#observer.observe(loader);
	}

	/**
	 * Disarm the loader observer and hide the loader — pagination is exhausted.
	 * Invariant counterpart to #armLoader: leaves `#observer === null`.
	 */
	#disarmLoader() {
		this.refs.loader?.setAttribute('hidden', '');
		this.#observer?.disconnect();
		this.#observer = null;
	}

	/**
	 * Signal for a page fetch: the component's abort controller combined with a
	 * per-request timeout. Falls back to the controller alone on browsers without
	 * AbortSignal.any (Safari < 17.4) — the request still aborts on filter reset
	 * and disconnect, just without the timeout ceiling.
	 * @returns {AbortSignal|undefined}
	 */
	#staggerEditorCards(band) {
		const cells = [...band.querySelectorAll('.cell--product')].slice(EDITOR_LIVE_CARDS);
		for (let i = 0; i < cells.length; i += EDITOR_LIVE_CARDS) {
			const chunk = cells.slice(i, i + EDITOR_LIVE_CARDS);
			const tpl = document.createElement('template');
			tpl.setAttribute('data-deferred-product', '');
			chunk[0].replaceWith(tpl);
			tpl.content.append(...chunk);
		}
		const reveal = () => {
			const tpl = band.querySelector('template[data-deferred-product]');
			if (!tpl || !band.isConnected) return;
			tpl.replaceWith(tpl.content);
			eventBus.emit(new CollectionUpdatedEvent({ data: { type: 'products-appended' } }));
			requestAnimationFrame(reveal);
		};
		requestAnimationFrame(reveal);
	}

	#fetchSignal() {
		const base = this.#abortController?.signal;
		if (typeof AbortSignal.any !== 'function' || typeof AbortSignal.timeout !== 'function') {
			return base;
		}
		const signals = [AbortSignal.timeout(FETCH_TIMEOUT_MS)];
		if (base) signals.push(base);
		return AbortSignal.any(signals);
	}

	/**
	 * Update the data-current-page attribute so the scroll store can read it.
	 */
	#updatePageAttribute() {
		this.dataset.currentPage = String(this.#currentPage);
	}

	/**
	 * Reset pagination state after filters are applied.
	 * Receives nextUrl from the event payload instead of reading from the DOM.
	 * Clears saved scroll data and re-initializes the observer.
	 * @param {string|null} nextUrl - Next page URL from the filter response
	 */
	#handleFilterReset(nextUrl) {
		// Invalidate any in-flight page load from the previous result set — if it
		// landed after this reset it would append stale (unfiltered) bands and
		// overwrite the filtered nextUrl with the old pagination chain.
		this.#generation++;
		this.#abortController?.abort();
		this.#abortController = new AbortController();

		// Failure/retry state belongs to the previous result set.
		clearTimeout(this.#retryTimer);
		this.#retryTimer = 0;
		this.#failedAttempts = 0;
		this.removeAttribute('data-load-error');

		this.#currentPage = 1;
		this.#updatePageAttribute();
		this.#nextUrl = nextUrl || null;
		this.#fetchedUrls.clear();

		// Embed injections don't carry across a filter change — they re-fire as the
		// user scrolls the new (filtered or cleared) results.
		this.#injection = null;
		this.#schedule.forEach((r) => {
			r.fired = false;
		});

		// Sync data attribute with the new nextUrl
		if (this.#nextUrl) {
			this.dataset.nextUrl = this.#nextUrl;
		} else {
			delete this.dataset.nextUrl;
		}

		// Clear saved scroll data since grid content has changed
		eventBus.emit(new ScrollClearEvent({ data: { pathname: location.pathname } }));

		this.#isLoading = false;

		// The grid is replaced by a SIBLING FilterApplied handler (the morph
		// effect), and eventBus.emit is a synchronous document.dispatchEvent — so
		// a microtask queued here runs AFTER the whole dispatch, i.e. after the
		// morph has landed, regardless of handler order. Reading the grid
		// synchronously here races the morph and, for a single-page filtered
		// result, disarms the loader against the stale (pre-filter) grid and
		// strands the deferred products.
		queueMicrotask(() => this.#afterFilterMorph());
	}

	/**
	 * Self-sync from data attributes when the ScrollRestoredEvent fired
	 * before this component's connectedCallback registered the listener.
	 */
	#selfSyncFromRestore() {
		const maxPage = parseInt(this.getAttribute('data-restore-max-page'), 10) || 1;
		const nextUrl = this.getAttribute('data-restore-next-url') || null;
		this.#applyRestoredState(maxPage, nextUrl);
	}

	/**
	 * Handle scroll:restored event — sync component state with restored data.
	 * The scroll store has already rebuilt the grid DOM and scrolled.
	 * @param {CustomEvent} event
	 */
	#handleScrollRestored(event) {
		const { maxPage, nextUrl } = event.detail.data;
		this.#applyRestoredState(maxPage, nextUrl);
	}

	/**
	 * Apply restored state — shared between event handler and self-sync.
	 * @param {number} maxPage
	 * @param {string|null} nextUrl
	 */
	#applyRestoredState(maxPage, nextUrl) {
		this.removeAttribute('data-restore-pending');
		this.removeAttribute('data-restore-max-page');
		this.removeAttribute('data-restore-next-url');

		// A restore only starts at module boot — if filters were applied while it
		// was rebuilding, its saved maxPage/nextUrl describe the pre-filter grid.
		if (this.#generation > 0) return;
		this.#currentPage = maxPage;
		this.#nextUrl = nextUrl;
		this.#updatePageAttribute();

		// The restored grid HTML contains every page band — register them all.
		this.#virtualizer?.refresh();

		// The restored HTML already contains any embed bands injected before navigating
		// away — mark those rules fired so they don't double-inject.
		this.#schedule.forEach((r) => {
			if (r.afterPage <= maxPage) r.fired = true;
		});

		if (this.#nextUrl) {
			this.dataset.nextUrl = this.#nextUrl;
		} else {
			delete this.dataset.nextUrl;
		}

		// Re-arm for continued infinite scroll, or disarm when exhausted.
		if (this.#nextUrl || this.refs.grid?.querySelector('template[data-deferred-product]')) {
			this.#armLoader();
		} else {
			this.#disarmLoader();
		}
	}

	/**
	 * Reconcile pagination with the freshly-morphed filtered grid. Runs in a
	 * microtask after the FilterApplied dispatch, so the grid morph has landed.
	 */
	#afterFilterMorph() {
		const grid = this.refs.grid;
		if (!grid) return;

		// A filtered result has no first-paint benefit to deferring — the user
		// already waited for the fetch. Reveal the page-1 remainder now so a narrow
		// filter never appears to return only `initial_count` products, and so the
		// reveal doesn't hinge on the loader's IntersectionObserver (which is paused
		// in background tabs).
		const deferred = grid.querySelectorAll('template[data-deferred-product]');
		if (deferred.length) {
			for (const tpl of deferred) tpl.replaceWith(tpl.content);
			eventBus.emit(new CollectionUpdatedEvent({ data: { type: 'products-appended' } }));
		}

		// Rebind the virtualizer to the new band(s) now in the DOM.
		this.#virtualizer?.reset();

		// Deferred is revealed, so the loader now only gates real next pages.
		this.#nextUrl = this.dataset.nextUrl || null;
		if (this.#nextUrl) {
			this.#armLoader();
		} else {
			this.#disarmLoader();
		}
	}

	/**
	 * Reveal deferred products or fetch the next page.
	 * Deferred products (wrapped in <template data-deferred-product>) are revealed
	 * instantly before making any network requests.
	 */
	async #loadMore() {
		if (this.#isLoading) return;

		const { grid } = this.refs;

		// First, reveal any deferred products from the initial page load
		const deferred = grid.querySelectorAll('template[data-deferred-product]');
		if (deferred.length > 0) {
			this.#isLoading = true;
			for (const tpl of deferred) {
				tpl.replaceWith(tpl.content);
			}
			eventBus.emit(new CollectionUpdatedEvent({ data: { type: 'products-appended' } }));
			this.#isLoading = false;

			if (this.#nextUrl || this.#morePending()) {
				this.#armLoader();
			} else {
				this.#disarmLoader();
			}
			return;
		}

		// --- Embed interleave schedule (Phase 2) ---
		// Embeds are removed in filtered views — load only host pages while filtering.
		const filtering = filterStore.getState().activeCount > 0;

		// Continue an in-progress embed injection.
		if (!filtering && this.#injection) {
			return this.#loadInjectionPage();
		}

		// Start an injection when a rule matches the host page just loaded.
		if (!filtering) {
			const due = this.#schedule.find((r) => !r.fired && r.afterPage === this.#currentPage);
			if (due) {
				this.#startInjection(due);
				return this.#loadInjectionPage();
			}
		}

		if (!this.#nextUrl) {
			// Host pages exhausted — fire any remaining embed rules at the very end.
			if (!filtering) {
				const remaining = this.#schedule.find((r) => !r.fired);
				if (remaining) {
					this.#startInjection(remaining);
					return this.#loadInjectionPage();
				}
			}
			// Nothing left to load (no host pages, no pending embeds).
			this.#disarmLoader();
			return;
		}

		// Prevent duplicate page fetches (Shopify pagination can wrap past last page)
		if (this.#fetchedUrls.has(this.#nextUrl)) {
			delete this.dataset.nextUrl;
			this.#nextUrl = null;
			if (this.#morePending()) {
				this.#armLoader();
			} else {
				this.#disarmLoader();
			}
			return;
		}

		this.#isLoading = true;
		const url = this.#nextUrl;
		this.#fetchedUrls.add(url);
		const generation = this.#generation;

		try {
			const response = await fetch(url, {
				signal: this.#fetchSignal(),
			});

			if (!response.ok) {
				throw new Error(`Failed to fetch: ${response.status}`);
			}

			const html = await response.text();

			// A filter reset replaced the grid while this page was in flight —
			// its bands and nextUrl belong to the previous result set.
			if (generation !== this.#generation) return;

			this.#failedAttempts = 0;
			this.removeAttribute('data-load-error');

			const parser = new DOMParser();
			const doc = parser.parseFromString(html, 'text/html');

			// Find the product grid in the fetched page
			const newGrid = doc.querySelector('[data-product-grid]');
			if (newGrid) {
				// Each fetched page renders one band — append it whole and register it
				// with the virtualizer so it can be recycled while off-screen.
				for (const band of [...newGrid.children]) {
					if (window.Shopify?.designMode) this.#staggerEditorCards(band);
					grid.appendChild(band);
					if (band.matches?.('[data-band]')) this.#virtualizer?.register(band);
				}

				// Notify observers (e.g. image preloader) that new cards were added
				eventBus.emit(new CollectionUpdatedEvent({ data: { type: 'products-appended' } }));
			}

			// Track page number
			this.#currentPage++;
			this.#updatePageAttribute();

			// Check for next page URL
			const newComponent = doc.querySelector('collection-infinite-scroll');
			this.#nextUrl = newComponent?.dataset.nextUrl || null;

			// Update data attribute for reference
			if (this.#nextUrl) {
				this.dataset.nextUrl = this.#nextUrl;
				// Re-arm to re-check intersection after the DOM grew.
				this.#armLoader();
			} else {
				delete this.dataset.nextUrl;
				if (this.#morePending()) {
					// Embed rules still to inject at the end — keep the loader alive.
					this.#armLoader();
				} else {
					this.#disarmLoader();
				}
			}
		} catch (error) {
			if (error.name !== 'AbortError') {
				console.error('CollectionInfiniteScroll: Failed to load more products', error);
				if (generation === this.#generation) {
					this.#handleLoadFailure(url);
				}
			}
		} finally {
			// Only release the lock for the generation that took it — a stale
			// load must not unlock a fetch started after the filter reset.
			if (generation === this.#generation) {
				this.#isLoading = false;
			}
		}
	}

	/**
	 * A page fetch failed (network error or non-OK status). Remove the URL from
	 * the dedupe set so it CAN be retried — otherwise the next intersection hits
	 * the duplicate guard, nulls nextUrl and kills pagination for the session.
	 * Auto-retries with exponential backoff up to MAX_LOAD_ATTEMPTS, then waits
	 * for a fresh user-driven intersection before starting a new cycle.
	 * @param {string} url - The URL that failed
	 */
	#handleLoadFailure(url) {
		this.#fetchedUrls.delete(url);
		this.#failedAttempts++;
		this.setAttribute('data-load-error', '');

		if (this.#failedAttempts >= MAX_LOAD_ATTEMPTS) {
			// Stop hammering — reset the counter so scrolling away and back
			// (an intersection transition) gives the page a fresh cycle.
			this.#failedAttempts = 0;
			return;
		}

		const generation = this.#generation;
		const delay = RETRY_BASE_DELAY_MS * 2 ** (this.#failedAttempts - 1);
		clearTimeout(this.#retryTimer);
		this.#retryTimer = window.setTimeout(() => {
			this.#retryTimer = 0;
			if (generation !== this.#generation || !this.isConnected) return;
			this.#armLoader();
		}, delay);
	}

	/**
	 * Read the Phase-2 embed interleave schedule from the DOM. Each
	 * [data-collection-embed-schedule] config injects N pages of another collection
	 * after a given host page.
	 */
	#readSchedule() {
		const configs = [...document.querySelectorAll('script[data-collection-embed-schedule]')]
			.map((el) => el.textContent);
		return parseEmbedSchedule(configs);
	}

	/**
	 * Begin an embed injection: append its full-width banner band, then arm the
	 * injection so subsequent loads pull the target collection's pages.
	 * @param {object} rule
	 */
	#startInjection(rule) {
		rule.fired = true;
		const tpl = rule.bannerId
			? document.querySelector(`template[data-collection-embed-banner="${rule.bannerId}"]`)
			: null;
		if (tpl) {
			const banner = document.createElement('div');
			banner.className = 'collection-embed-banner-band';
			banner.setAttribute('data-band', '');
			banner.setAttribute('data-embed-band', '');
			banner.setAttribute('data-embed-banner', '');
			banner.appendChild(tpl.content.cloneNode(true));
			this.refs.grid.appendChild(banner);
			this.#virtualizer?.register(banner);
		}
		this.#injection = { rule, nextUrl: rule.targetUrl, loaded: 0 };
	}

	/**
	 * Load the next page of the currently-injecting target collection as a band in the
	 * host grid. Ends the injection when the page cap or the target is exhausted, then
	 * lets the host resume.
	 */
	async #loadInjectionPage() {
		if (this.#isLoading) return;
		const inj = this.#injection;
		if (!inj) return;

		const capReached = inj.rule.pageCount > 0 && inj.loaded >= inj.rule.pageCount;
		if (capReached || !inj.nextUrl || this.#fetchedUrls.has(inj.nextUrl)) {
			this.#injection = null;
			this.#armLoader();
			return;
		}

		this.#isLoading = true;
		const url = inj.nextUrl;
		this.#fetchedUrls.add(url);
		const generation = this.#generation;

		try {
			const response = await fetch(url, { signal: this.#fetchSignal() });
			if (!response.ok) throw new Error(`Failed to fetch embed page: ${response.status}`);

			const text = await response.text();

			// A filter reset replaced the grid while this embed page was in flight.
			if (generation !== this.#generation) return;

			const doc = new DOMParser().parseFromString(text, 'text/html');
			const newGrid = doc.querySelector('[data-product-grid]');
			if (newGrid) {
				for (const tpl of newGrid.querySelectorAll('template[data-deferred-product]')) {
					tpl.replaceWith(tpl.content);
				}
				for (const band of [...newGrid.children]) {
					if (!band.matches?.('[data-band]')) continue;
					band.setAttribute('data-embed-band', '');
					this.refs.grid.appendChild(band);
					this.#virtualizer?.register(band);
				}
				eventBus.emit(new CollectionUpdatedEvent({ data: { type: 'products-appended' } }));
			}

			inj.loaded++;
			const nextComponent = doc.querySelector('collection-infinite-scroll');
			inj.nextUrl = nextComponent?.dataset.nextUrl || null;
		} catch (error) {
			if (error.name !== 'AbortError') {
				console.error('CollectionInfiniteScroll: embed injection failed', error);
				// Let a future injection retry this page instead of dupe-guarding it.
				this.#fetchedUrls.delete(url);
			}
			if (generation === this.#generation) this.#injection = null;
		} finally {
			// Only release the lock for the generation that took it — a stale
			// load must not unlock a fetch started after the filter reset.
			if (generation === this.#generation) {
				this.#isLoading = false;
				this.#armLoader();
			}
		}
	}

	/**
	 * Whether there's still work beyond host pages — an active injection or an unfired
	 * embed rule. Used to keep the loader alive after host pages exhaust so a rule whose
	 * afterPage exceeds the host's last page still injects at the end.
	 */
	#morePending() {
		if (this.#injection) return true;
		const filtering = filterStore.getState().activeCount > 0;
		return !filtering && this.#schedule.some((r) => !r.fired);
	}
}

if (!customElements.get('collection-infinite-scroll')) {
	customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
}
